import type { ReactNode } from 'react';
import type { QuestionPurpose } from '@course-authoring/shared-types';
import { AUTHOR_WITH_AI_QUESTION_TYPES, type AuthorWithAiQuestionType } from '../../api/authorWithAiApi';
import type { LibraryConcept } from '../../api/centerstageApi';
import { CenterstageFindBar } from '../centerstage/CenterstageFindBar';
import { QUESTION_BANK_BROWSE_COPY as COPY, QUESTION_BANK_TYPE_LABELS } from './questionBankBrowseCopy';
import { QuestionBankFilterSelect } from './QuestionBankFilterSelect';
import type { QuestionBankBrowseStyles } from './questionBankBrowseStyles';

export interface QuestionBankFindRowProps {
  styles: QuestionBankBrowseStyles;
  /** What is typed in the field; only Enter turns it into the search term. */
  draft: string;
  onDraftChange: (draft: string) => void;
  searchTerm: string;
  onCommitSearch: (term: string) => void;
  /** The concepts the autosuggest offers (only those with questions). */
  concepts: readonly LibraryConcept[];
  categoryNames: ReadonlyMap<number, string>;
  onPickConcept: (conceptId: number) => void;
  onBrowseConcepts: () => void;
  searchPlaceholder: string;
  blockType: AuthorWithAiQuestionType;
  onTypeChange: (type: AuthorWithAiQuestionType) => void;
  /** The purposes to choose between; with fewer than two, Purpose shows `purpose` as fixed text. */
  purposes: readonly QuestionPurpose[];
  purpose: QuestionPurpose;
  onPurposeChange: (purpose: QuestionPurpose) => void;
  /** A control after Purpose: the picker's "Skills & risk areas" pill. The page has none; its panel sits beside the list. */
  taxonomyFilter?: ReactNode;
}

/**
 * The bank's find row: the search field with its concept autosuggest and
 * "or browse all concepts", then the Type select and the Purpose control,
 * neither of which is ever empty, then whatever the caller places after
 * them. Owns no filter state.
 */
export function QuestionBankFindRow({
  styles,
  draft,
  onDraftChange,
  searchTerm,
  onCommitSearch,
  concepts,
  categoryNames,
  onPickConcept,
  onBrowseConcepts,
  searchPlaceholder,
  blockType,
  onTypeChange,
  purposes,
  purpose,
  onPurposeChange,
  taxonomyFilter,
}: QuestionBankFindRowProps) {
  const b = styles.browse;
  return (
    <div style={b.findRow}>
      <CenterstageFindBar
        styles={styles.centerstage}
        draft={draft}
        onDraftChange={onDraftChange}
        searchTerm={searchTerm}
        onCommitSearch={onCommitSearch}
        concepts={concepts}
        categoryNames={categoryNames}
        onPickConcept={onPickConcept}
        onBrowseConcepts={onBrowseConcepts}
        labels={{ searchLabel: COPY.searchLabel, searchPlaceholder }}
      />
      <div style={b.filters}>
        <QuestionBankFilterSelect styles={b} name={COPY.typeLabel} value={blockType} onChange={(value) => onTypeChange(value as AuthorWithAiQuestionType)}>
          {AUTHOR_WITH_AI_QUESTION_TYPES.map((type) => (
            <option key={type} value={type}>{QUESTION_BANK_TYPE_LABELS[type] ?? type}</option>
          ))}
        </QuestionBankFilterSelect>
        {purposes.length > 1 ? (
          <QuestionBankFilterSelect styles={b} name={COPY.purposeLabel} value={purpose} onChange={(value) => onPurposeChange(value as QuestionPurpose)}>
            {purposes.map((option) => <option key={option} value={option}>{option}</option>)}
          </QuestionBankFilterSelect>
        ) : (
          <span style={b.fieldFixed} data-testid="purpose-fixed">
            <span style={b.fieldName}>{`${COPY.purposeLabel}:`}</span>
            <span style={b.fieldValue}>{purpose}</span>
          </span>
        )}
        {taxonomyFilter}
      </div>
    </div>
  );
}
